import { LocaleLink } from "@i18n/routing";
import NextLink from "next/link";
import type { ComponentProps } from "react";
import { isUnlocalizedPath } from "./lib/unlocalized-paths";

/**
 * The intersection, not either side: `next/link` and next-intl's `Link` agree
 * on the anchor props but not on `prefetch`, so widening to one of them alone
 * makes the other unassignable.
 */
type SiteLinkProps = Omit<ComponentProps<typeof NextLink>, "href"> &
	Omit<ComponentProps<typeof LocaleLink>, "href"> & {
		href: string;
	};

/**
 * Internal link that picks its own wrapper.
 *
 * Marketing components are handed hrefs as data — a `primaryHref` prop, a CTA
 * config object — so the component cannot know whether a given destination is
 * locale-prefixed. `LocaleLink` for `/auth/signup` produces a 404 and plain
 * `next/link` for `/pricing` drops the visitor's locale; both are silent.
 * `SiteLink` decides per href instead, and is the right default anywhere a
 * destination arrives from outside the component.
 *
 * @see isUnlocalizedPath
 */
export function SiteLink({ href, ...props }: SiteLinkProps) {
	if (isUnlocalizedPath(href)) {
		return <NextLink href={href} {...props} />;
	}

	return <LocaleLink href={href} {...props} />;
}
